import { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Tabs,
  Group,
  Stack,
  Text,
  Button,
  Avatar,
  Badge,
  Loader,
  Center,
  Textarea,
  Radio,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { MessageCircle, Clock } from 'react-feather';
import { coachPortalApi } from '../../../services/api/coachPortal';
import type { CoachHuddle } from '../../../types';
import { formatDate, formatTime } from '../../../utils/formatters';

interface HuddleDetailModalProps {
  opened: boolean;
  onClose: () => void;
  huddleId: string | null;
  onSuccess?: () => void;
}

const HUDDLE_TYPE_LABELS: Record<string, string> = {
  intro: 'INTRO',
  midpoint_checkin: 'MIDPOINT',
  general: 'CHECK IN',
};

export function HuddleDetailModal({
  opened,
  onClose,
  huddleId,
  onSuccess,
}: HuddleDetailModalProps) {
  const [huddle, setHuddle] = useState<CoachHuddle | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>('attendance');

  // Attendance form state
  const [attendanceStatus, setAttendanceStatus] = useState<'attended' | 'did_not_attend' | ''>('');
  const [attendanceNotes, setAttendanceNotes] = useState('');
  const [savingAttendance, setSavingAttendance] = useState(false);

  // Feedback form state
  const [feedbackNotes, setFeedbackNotes] = useState('');
  const [savingFeedback, setSavingFeedback] = useState(false);

  // Load huddle details
  const loadHuddle = useCallback(async () => {
    if (!huddleId) return;
    setLoading(true);
    try {
      const data = await coachPortalApi.getHuddleById(huddleId);
      setHuddle(data);

      // Populate attendance form from existing data
      if (data.attendance_status !== 'pending') {
        setAttendanceStatus(data.attendance_status as 'attended' | 'did_not_attend');
      } else {
        setAttendanceStatus('');
      }
      setAttendanceNotes(data.attendance_notes || '');
      setFeedbackNotes(data.feedback_notes || '');
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to load check in details.',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [huddleId]);

  useEffect(() => {
    if (opened && huddleId) {
      loadHuddle();
      setActiveTab('attendance');
    }
  }, [opened, huddleId, loadHuddle]);

  const handleSaveAttendance = async () => {
    if (!huddleId || !attendanceStatus) return;

    setSavingAttendance(true);
    try {
      const updated = await coachPortalApi.markHuddleAttendance(huddleId, {
        status: attendanceStatus,
        notes: attendanceNotes || undefined,
      });
      setHuddle(updated);
      notifications.show({
        title: 'Attendance Saved',
        message: 'Check in attendance has been recorded.',
        color: 'green',
      });
      onSuccess?.();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to save attendance.',
        color: 'red',
      });
    } finally {
      setSavingAttendance(false);
    }
  };

  const handleSaveFeedback = async () => {
    if (!huddleId) return;

    setSavingFeedback(true);
    try {
      const updated = await coachPortalApi.saveHuddleFeedback(huddleId, {
        notes: feedbackNotes,
      });
      setHuddle(updated);
      notifications.show({
        title: 'Feedback Saved',
        message: 'Post-check in notes have been saved.',
        color: 'green',
      });
      onSuccess?.();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to save feedback.',
        color: 'red',
      });
    } finally {
      setSavingFeedback(false);
    }
  };

  const handleClose = () => {
    setHuddle(null);
    setAttendanceStatus('');
    setAttendanceNotes('');
    setFeedbackNotes('');
    onClose();
  };

  const initials = huddle
    ? (huddle.participant_name || '')
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : '';

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <MessageCircle size={18} />
          <Text fw={600}>Check In Details</Text>
        </Group>
      }
      size="md"
    >
      {loading ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : huddle ? (
        <Stack gap="md">
          {/* Header info */}
          <Stack gap="xs">
            <Group gap="sm">
              <Badge size="sm" variant="light" color="blue">
                {HUDDLE_TYPE_LABELS[huddle.huddle_type] || 'CHECK IN'}
              </Badge>
              <Text fw={600} size="lg">
                {huddle.title}
              </Text>
            </Group>

            <Group gap="sm">
              <Clock size={14} color="var(--mantine-color-gray-6)" />
              <Text size="sm" c="dimmed">
                {formatDate(huddle.scheduled_start)} at {formatTime(huddle.scheduled_start)} &middot;{' '}
                {huddle.duration_minutes} min
              </Text>
            </Group>

            <Group gap="sm">
              <Avatar size="sm" radius="xl" color="blue">
                {initials}
              </Avatar>
              <div>
                <Text size="sm" fw={500}>
                  {huddle.participant_name}
                </Text>
                {huddle.participant_title && (
                  <Text size="xs" c="dimmed">
                    {huddle.participant_title}
                  </Text>
                )}
              </div>
            </Group>
          </Stack>

          {/* Tabs */}
          <Tabs value={activeTab} onChange={setActiveTab}>
            <Tabs.List>
              <Tabs.Tab value="attendance">Attendance</Tabs.Tab>
              <Tabs.Tab value="feedback">Coach Feedback</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="attendance" pt="md">
              <Stack gap="md">
                <Radio.Group
                  label="Attendance Status"
                  value={attendanceStatus}
                  onChange={(value) =>
                    setAttendanceStatus(value as 'attended' | 'did_not_attend')
                  }
                >
                  <Stack gap="xs" mt="xs">
                    <Radio value="attended" label="Attended" />
                    <Radio value="did_not_attend" label="Did Not Attend" />
                  </Stack>
                </Radio.Group>

                <Textarea
                  label="Notes (optional)"
                  placeholder="Any notes about the check in attendance..."
                  value={attendanceNotes}
                  onChange={(e) => setAttendanceNotes(e.currentTarget.value)}
                  minRows={3}
                />

                <Group justify="flex-end">
                  <Button
                    onClick={handleSaveAttendance}
                    loading={savingAttendance}
                    disabled={!attendanceStatus}
                  >
                    Save Attendance
                  </Button>
                </Group>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="feedback" pt="md">
              <Stack gap="md">
                <Textarea
                  label="Post-Check In Notes"
                  placeholder="Enter your notes from this check in..."
                  value={feedbackNotes}
                  onChange={(e) => setFeedbackNotes(e.currentTarget.value)}
                  minRows={5}
                />

                <Group justify="flex-end">
                  <Button onClick={handleSaveFeedback} loading={savingFeedback}>
                    Save Feedback
                  </Button>
                </Group>
              </Stack>
            </Tabs.Panel>
          </Tabs>
        </Stack>
      ) : null}
    </Modal>
  );
}

export default HuddleDetailModal;
